import { apiRequest } from './api';
import type { AuthUser } from './auth-api';
import type { Organization } from './organization-api';

export type NotificationCategory = 'approval' | 'book' | 'security' | 'system';

export type NotificationType =
  | 'approval_pending'
  | 'delete_approval_pending'
  | 'approval_approved'
  | 'approval_rejected'
  | 'publish_ready'
  | 'book_published'
  | 'book_processing'
  | 'book_ready'
  | 'book_failed'
  | 'book_expiring'
  | 'security_alert'
  | 'system';

export interface AppNotification {
  id: string;
  type: NotificationType;
  category: NotificationCategory;
  title: string;
  message: string;
  createdAt: string;
  organizationId: number | null;
  organizationName?: string | null;
  organizationLogo?: string | null;
  organizationColor?: string | null;
  userId?: number | null;
  targetRoles?: string[];
  targetPermissions?: string[];
  link: string;
  linkText?: string;
  isRead: boolean;
  isDismissed: boolean;
  metadata?: {
    bookId?: number;
    bookTitle?: string;
    approvalId?: number;
    approvalLevel?: number;
    requesterName?: string;
    requesterEmail?: string;
    expiryDate?: string;
    reason?: string;
    status?: string;
  };
}

export type NotificationFilter = 'all' | 'unread' | 'approval' | 'book' | 'alert';

export const NOTIFICATIONS_REFRESH_EVENT = 'flipbook:notifications-refresh';

export function dispatchNotificationsRefresh() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(NOTIFICATIONS_REFRESH_EVENT));
  }
}

/**
 * Format relative time (e.g. "Just now", "5m ago", "2h ago", "3d ago")
 */
export function formatRelativeTime(dateString: string): string {
  try {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    if (isNaN(diffMs)) return 'Recently';

    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 45) return 'Just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    const diffWeeks = Math.floor(diffDays / 7);
    if (diffWeeks < 4) return `${diffWeeks}w ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return 'Recently';
  }
}

export interface FetchNotificationsParams {
  user: AuthUser | null;
  organizationId: number | 'all';
  organizations: Organization[];
  page?: number;
  limit?: number;
}

export interface FetchNotificationsResult {
  notifications: AppNotification[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    has_more: boolean;
  };
  unreadCount: number;
}

/**
 * Dynamically aggregates live notifications from the backend database APIs with pagination support.
 */
export async function fetchLiveNotifications({
  user,
  organizationId,
  organizations,
  page = 1,
  limit = 20,
}: FetchNotificationsParams): Promise<FetchNotificationsResult> {
  const fallbackResult: FetchNotificationsResult = {
    notifications: [],
    pagination: {
      page: 1,
      limit,
      total: 0,
      has_more: false,
    },
    unreadCount: 0,
  };

  if (!user) return fallbackResult;

  const orgMap = new Map<number, Organization>();
  organizations.forEach((org) => {
    orgMap.set(org.id, org);
  });

  const getOrgDetails = (orgId?: number | null) => {
    if (!orgId) return { name: null, logo: null, color: null };
    const org = orgMap.get(orgId);
    return {
      name: org?.name || null,
      logo: org?.logo || null,
      color: org?.primary_color || null,
    };
  };

  try {
    const qsParts: string[] = [`page=${page}`, `limit=${limit}`];
    if (organizationId !== 'all') {
      qsParts.push(`organization_id=${organizationId}`);
    }
    const qs = `?${qsParts.join('&')}`;

    const res = await apiRequest<any>(`/api/notifications${qs}`);

    if (res && res.success && Array.isArray(res.data)) {
      const items: AppNotification[] = res.data.map((item: any) => {
        const orgInfo = getOrgDetails(item.organizationId);
        return {
          ...item,
          id: String(item.id),
          organizationName: item.organizationName || orgInfo.name,
          organizationLogo: item.organizationLogo || orgInfo.logo,
          organizationColor: item.organizationColor || orgInfo.color,
          isRead: Boolean(item.isRead),
          isDismissed: false,
        };
      });

      const pagination = res.pagination || {
        page,
        limit,
        total: items.length,
        has_more: false,
      };

      const unreadCount = typeof res.unread_count === 'number'
        ? res.unread_count
        : items.filter((n) => !n.isRead).length;

      return {
        notifications: items,
        pagination,
        unreadCount,
      };
    }
  } catch (err) {
    console.error('Failed to fetch notifications from backend DB API', err);
  }

  return fallbackResult;
}

/**
 * Mark notification as read in the database.
 */
export async function apiMarkNotificationAsRead(id: string): Promise<void> {
  try {
    await apiRequest(`/api/notifications/${id}/read`, { method: 'PATCH' });
  } catch (err) {
    console.error('Failed to mark notification read in DB', err);
  }
}

/**
 * Mark all notifications as read in the database for current scope.
 */
export async function apiMarkAllNotificationsAsRead(organizationId?: number | 'all'): Promise<void> {
  try {
    const qs = organizationId && organizationId !== 'all' ? `?organization_id=${organizationId}` : '';
    await apiRequest(`/api/notifications/mark-all-read${qs}`, { method: 'POST' });
  } catch (err) {
    console.error('Failed to mark all notifications read in DB', err);
  }
}

/**
 * Clear all read notifications in the database.
 */
export async function apiClearReadNotifications(organizationId?: number | 'all'): Promise<void> {
  try {
    const qs = organizationId && organizationId !== 'all' ? `?organization_id=${organizationId}` : '';
    await apiRequest(`/api/notifications/clear-read${qs}`, { method: 'POST' });
  } catch (err) {
    console.error('Failed to clear read notifications in DB', err);
  }
}

/**
 * Delete / dismiss a notification in the database.
 */
export async function apiDismissNotification(id: string): Promise<void> {
  try {
    await apiRequest(`/api/notifications/${id}`, { method: 'DELETE' });
  } catch (err) {
    console.error('Failed to delete notification in DB', err);
  }
}
